import defaultAttributes from "backend/defaultAttributes";
import useComments from "backend/hooks/comments/useComments";
import useElement from "backend/hooks/elements/useElement";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import useAttachments from "backend/hooks/files/useAttachments";
import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import { ICardElementWithChildren } from "backend/model/model";
import PerformanceTrend from "components/PerformanceTrend";
import InitiativeToolbar from "components/toolbars/InitiativeToolbar";
import AnalysisCard from "design/components/cards/AnalysisCard/AnalysisCard";
import BudgetUtilizationCard from "design/components/cards/BudgetUtilizationCard/BudgetUtilizationCard";
import InitiativeProgressCard from "design/components/cards/InitiativeProgressCard/InitiativeProgressCard";
import MeasureInfo from "design/components/cards/MeasureInfo";
import SlippageCard from "design/components/cards/SlippageCard/SlippageCard";
import HorizontalGrid from "design/components/grids/HorizontalGrid";
import ItemDrawer from "design/components/menus/ItemDrawer";
import ProgressBar from "design/components/progress/ProgressBar";
import MeasureTable from "design/components/tables/MeasureTable";
import InitiativeHeading from "design/components/texts/InitiativeHeading";
import LeftMenuPosition from "design/layouts/LeftMenuPosition";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import Page from "design/pages/Page";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import * as conf from "qpr-base/configuration";
import { IChartSettings } from "qpr-base/configuration/types";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import useQueryBuilder from "qpr-base/querybuilder/useQueryBuilder";
import Anchor from "qpr-base/ui/Anchor";
import UploadedFilesList from "qpr-base/ui/components/UploadedFilesList";
import PageContentMenu from "qpr-base/uicomponents/view/PageContentMenu";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { formatDate, getDateFromMEAValue } from "utils/utils";
import useCreateAction from "backend/hooks/actions/useCreateAction";
import useAllActions, { type ActionData } from "backend/hooks/actions/useAllActions";
import ActionDialog from "components/forms/valueinput/ActionDialog";
import ActionsPreview from "design/components/cards/ActionsPreview";

const useStyles = makeThemedStyles("InitiativeDetails", (theme) => ({
	title: {
		fontSize: "1.828em",
		lineHeight: "1.5em",
		letterSpacing: "0.05625px",
		color: "rgba(63, 69, 79, 1)",
		margin: 0,
		marginBottom: "0.625em",
		fontWeight: 600,
	},
	table: {
		"& .MuiPaper-root": {
			all: "unset",
		},
		"& .MuiTableCell-stickyHeader": {
			backgroundColor: "unset",
		},
		"& .MuiTableCell-head": {
			textTransform: "uppercase",
			fontSize: "14px",
			color: "black",
		},
		"& .actProgCell": {
			borderInlineStart: "1px solid #e0e0e0",
		},
	},
}));

const CHARTSETTINGS: IChartSettings = {
	series: [
		{ symbol: "PLANNED_COMPLETION", color: "rgb(93,98,104)" },
		{
			symbol: "COMPLETION_PERCENTAGE",
			color: "rgba(38, 166, 242, 0.8)",
		},
	],
};

const InitiativeDetails = () => {
	const styles = useStyles();
	const ctx = useAppContext();
	const link = useLink();
	const history = useHistory();
	const { data: comments } = useComments({
		id: ctx.elementId || "",
		period: ctx.period || "",
	});
	const { data: attachments } = useAttachments({
		elementId: ctx.elementId,
		period: ctx.period,
	});
	const [firstComment] = comments || [];
	const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);

	const { data: allActions } = useAllActions();
	const actions: ActionData[] | undefined = useMemo(
		() =>
			allActions?.filter(
				(action) => action.searchKpiInitiativeId === (ctx.elementId || "")
			),
		[allActions, ctx.elementId]
	);

	const createActionMutation = useCreateAction();

	const initiativeInfo = useMemo(() => ({
		id: ctx.elementId || "",
		typename: "Initiative" as const,
		name: ctx.elementId ? "Initiative" : "",
	}), [ctx.elementId]);

	const { data: periodlevels } = usePeriodLevels();

	const {
		data: mea,
		isLoading,
		isFetching: isElementFetching,
		isRefetching,
	} = useElement(ctx.elementId);
	const { data: meaParent } = useElement(mea ? mea.parent_element_id : "");
	const {
		data: scorecardInitiatives,
		isLoading: isScorecardKpisLoading,
		isFetching,
	} = useQueryBuilder<ICardElementWithChildren[]>((q) =>
		q(ctx.elementId)
			.scorecard()
			.attributes(...defaultAttributes(ctx.period, []))
			.measures({ criteria: { typename: "Initiative" } })
	);
	const { isLoading: isChildrenLoading, data: children } = useHierarchy(
		ctx.elementId
	);
	const milestones = React.useMemo(() => {
		if (children) {
			return children.filter((c) => c.typename === "Milestone");
		}
		return [];
	}, [children]);

	// not sure why this is needed here...
	if (!mea) {
		return null;
	}
	return (
		<Page
			isLoading={
				isLoading ||
				isScorecardKpisLoading ||
				isChildrenLoading ||
				isFetching ||
				isElementFetching ||
				isRefetching
			}
			Toolbar={InitiativeToolbar}
		>
			<SplitView left="180px" right="1100px" gap="20px">
				<div>
					<LeftMenuPosition>
						<ItemDrawer
							title={t("initiatives.Initiatives")}
							items={scorecardInitiatives?.data || []}
							onSelect={(item) => {
								link({ meaId: item.id });
							}}
						/>
						<PageContentMenu
							componentData={{
								items: [
									{ title: t("initiatives.Initiative Info"), anchor: "header" },
									{
										title: t("initiatives.Performance"),
										anchor: "performance",
									},
									{
										title: t("initiatives.Progress Trend"),
										anchor: "progress-trend",
									},
									{ title: t("initiatives.Milestones"), anchor: "milestones" },
									{ title: t("initiatives.Analysis"), anchor: "justification" },
								],
							}}
						/>
					</LeftMenuPosition>
				</div>
				<div>
					<StackView>
						<Anchor name="header" />
						<MeasureInfo
							title={t("initiatives.Intitiative Information")}
							items={[
								{
									label: t("initiatives.Timeline"),
									text: `${t("initiatives.Start Date")} ${formatDate(
										getDateFromMEAValue(mea?.START_DATE),
										// mea?.START_DATE_prettyvalue || "",
										t("configtypes.en-GB"),
										true
									)}\n${t("initiatives.End Date")} ${formatDate(
										getDateFromMEAValue(mea?.END_DATE),
										t("configtypes.en-GB"),
										true
									)}`,
								},
								{
									label: t("initiatives.Strategic Objective"),
									text:
										(mea?.parentTypename || "") === "Strategic Objective"
											? (getTranslatedAttribute(
												meaParent,
												"Name",
												ctx.language
											) || "") +
											"\n" +
											getTranslatedAttribute(
												meaParent,
												"Description",
												ctx.language
											)
											: "",
								},
								{
									label: t("initiatives.Description"),
									text:
										getTranslatedAttribute(mea, "Description", ctx.language) ||
										"",
								},
								/*{
								  label: t("initiatives.Measurement Frequency"),
								  text: mea?.periodlevelname || "",
								},*/
							]}
						/>
						<Anchor name="performance" />
						<HorizontalGrid>
							<div style={{ height: "250px" }}>
								<InitiativeProgressCard
									data={mea}
									heading={t("initiatives.Progress")}
									series={[
										{
											title: t("initiatives.Planned Progress"),
											prettyvalue: "PLANNED_COMPLETION_prettyvalue",
										},
										{
											title: t("initiatives.Variance"),
											prettyvalue: "VARIANCE_prettyvalue",
										},
									]}
									display={{
										title: mea.COMPLETION_PERCENTAGE_prettyvalue,
										value: "COMPLETION_PERCENTAGE",
										colorKey: "rangecolor",
										bgColorKey: "rangecolor",
									}}
								/>
							</div>
							<div style={{ height: "250px" }}>
								<SlippageCard
									data={mea}
									dateDisplay={{
										title: t("initiatives.Estimated End Date"),
										value: "ACTUAL_END_DATE",
									}}
									display={{
										title: t("initiatives.Slippage"),
										unit: "SLIPPAGE_unit",
										value: "SLIPPAGE",
										valuewithoutsymbol: "SLIPPAGE_valuewithoutsymbol",
										unitConversionFunction: (str: string) => {
											const slippageUnits: Record<string, string> = {
												d: t("initiatives.Days"),
												w: t("initiatives.Weeks"),
												y: t("initiatives.Years"),
											};
											return slippageUnits[str] || str;
										},
									}}
								/>
							</div>
							<div style={{ height: "250px" }}>
								<BudgetUtilizationCard
									initiative={mea}
									settings={conf.getInitiativeDetailsViewGaugeSettings(mea)}
								/>
							</div>
						</HorizontalGrid>
						<div>
							<Anchor name="progress-trend" />
							<InitiativeHeading>
								{t("initiatives.Progress Trend")}
							</InitiativeHeading>

							<PerformanceTrend
								element={mea}
								settings={CHARTSETTINGS}
								width="1090px"
							/>
						</div>

						<Anchor name="milestones" />

						<InitiativeHeading>{t("initiatives.Milestones")}</InitiativeHeading>
						<MeasureTable
							className={styles.table}
							items={milestones}
							columns={[
								{
									title: () => <div>{t("initiatives.Period")}</div>,
									value: (row) => <div>{row.elem.period?.name || ""}</div>,
								},
								{
									title: () => <div>{t("initiatives.Name")}</div>,
									value: (props) => <div>{props.elem.name}</div>,
								},
								{
									title: () => <div>{t("initiatives.Actual Progress")}</div>,
									tdClassName: "actProgCell",
									value: (props) =>
										mea.STATUS === "0" ? (
											<div />
										) : (
											<ProgressBar
												barColor={`#${props.elem.rangecolor}`}
												value={Number(props.elem.COMPLETION_PERCENTAGE) || 0}
												title={
													props.elem.COMPLETION_PERCENTAGE_prettyvalue ||
													"0.00 %"
												}
												titleJustify="center"
											/>
										),
								},
								{
									title: () => <div>{t("initiatives.Planned Progress")}</div>,
									value: (props) =>
										mea.STATUS === "0" ? (
											<div />
										) : (
											<ProgressBar
												barColor={`#${props.elem.rangecolor}`}
												value={Number(props.elem.PLANNED_COMPLETION) || 0}
												title={
													props.elem.PLANNED_COMPLETION_prettyvalue || "0.00 %"
												}
												titleJustify="center"
											/>
										),
								},
								{
									title: () => <div>{t("initiatives.Variance")}</div>,
									value: (props) => (
										<div>{props.elem.VARIANCE_prettyvalue}</div>
									),
								},
							]}
						/>

						<Anchor name="justification" />
						<InitiativeHeading subTitle={ctx.period}>
							{t("initiatives.Analysis")}
						</InitiativeHeading>
						{firstComment && mea.STATUS === "1" ? (
							<AnalysisCard
								userData={{
									name: firstComment.modifiedby || firstComment.createdby,
									subtitle: t("initiatives.Owner"),
								}}
								date={new Date(
									firstComment.lastmodifieddate
								).toLocaleDateString()}
								description={
									firstComment
										? firstComment.description
										: t("initiatives.No Comments yet")
								}
							>
								<UploadedFilesList values={attachments} readOnly />
							</AnalysisCard>
						) : (
							<AnalysisCard description={t("initiatives.No Comments Yet")}>
								{mea.STATUS === "1" ? (
									<UploadedFilesList values={attachments} readOnly />
								) : null}
							</AnalysisCard>
						)}

						{/* New Actions Section */}
						<InitiativeHeading>{t("Actions.Actions")}</InitiativeHeading>
						<div style={{ marginTop: "16px", marginBottom: "24px" }}>
							<Button
								variant="contained"
								color="primary"
								startIcon={<AddIcon />}
								onClick={() => setIsActionDialogOpen(true)}
								style={{ marginBottom: "16px" }}
							>
								{t("Add New Action")}
							</Button>
							<ActionsPreview
								actions={actions || []}
								kpiInitiativeName={initiativeInfo.name}
							/>
						</div>
						<ActionDialog
							open={isActionDialogOpen}
							onClose={() => setIsActionDialogOpen(false)}
							kpiOrInitiative={initiativeInfo}
						/>

						<div style={{ height: "500px" }} />
					</StackView>
				</div>
			</SplitView>
		</Page>
	);
};

export default InitiativeDetails;
